import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Avatar,
  Badge,
  Button,
  Stack,
} from '@mui/material';
import ApplyLeaveModal from './Leaves/ApplyLeaveModal';
import {
  useLeaveCCrequests,
  useLeaveRequestsforme,
  useSingleEmpLeaveDetails,
  useUpdateEmpLeaveRequest
} from '../../hooks/useLeaves';
import { useGetSingleEmployee } from '../../hooks/useEmployees';
import { AuthContext } from '../../context/logeduser';
import { leaveContext } from '../../context/leavecontext';
import { formatDate } from '../../utils/dateFormat';
import PageHeader from './PageHeader';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// Icons
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventBusyIcon from '@mui/icons-material/EventBusy';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
// Same soft FORTHEYE-style cards used on /home so the portal feels like one app.
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

// Soft status palette (tint bg + solid fg), matches the home leave card.
const STATUS = {
  ACCEPTED: { fg: '#16a34a', bg: '#f0fdf4', label: 'Accepted' },
  PENDING: { fg: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
  DECLINED: { fg: '#ef4444', bg: '#fef2f2', label: 'Declined' },
  CANCELLED: { fg: '#8b5cf6', bg: '#f5f3ff', label: 'Cancelled' },
  WITHDRAWN: { fg: '#8b5cf6', bg: '#f5f3ff', label: 'Withdrawn' },
};
// Auto-approved (stale-pending, no manual review) shows its own label/colour
// so it reads distinctly from a manually-accepted request.
const statusMeta = (s, isAutoApproved = false) => {
  if (s === 'ACCEPTED' && isAutoApproved) {
    return { fg: '#0d9488', bg: '#f0fdfa', label: 'Auto Accept' };
  }
  return STATUS[s] || { fg: '#6b7280', bg: '#f3f4f6', label: s };
};

// Reusable counter pill (mirrors /home StatPill).
const StatPill = ({ value, label, dot }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.7rem' }, lineHeight: 1, color: '#111827' }}>
        {value}
      </Typography>
      {dot && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
    </Box>
    <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.73rem', mt: 0.4 }}>{label}</Typography>
  </Box>
);

// Soft empty state (replaces the broken external SVG).
const EmptyState = ({ message }) => (
  <Box sx={{ py: 7, textAlign: 'center' }}>
    <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
      <EventBusyIcon sx={{ fontSize: 30 }} />
    </Box>
    <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>{message}</Typography>
  </Box>
);

// Filter pills (Applied / Accepted / Pending / Rejected) — soft tinted when active.
const FILTERS = [
  { value: 'applied', label: 'Applied (All)', icon: <AssignmentIcon sx={{ fontSize: 18 }} />, fg: ACCENT, bg: '#eef4ff' },
  { value: 'accepted', label: 'Accepted', icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, fg: '#16a34a', bg: '#f0fdf4' },
  { value: 'pending', label: 'Pending', icon: <PendingIcon sx={{ fontSize: 18 }} />, fg: '#f59e0b', bg: '#fffbeb' },
  { value: 'rejected', label: 'Rejected', icon: <CancelIcon sx={{ fontSize: 18 }} />, fg: '#ef4444', bg: '#fef2f2' },
];

// ---- CC / received request card (managers acting on others' requests) ----
const LeaveRequestCard = ({ request, onStatusChange, type = 'received' }) => {
  const [expanded, setExpanded] = useState(false);
  const [localStatus, setLocalStatus] = useState(request.status);
  const meta = statusMeta(localStatus, request.is_auto_approved);

  const calculateDays = () => {
    // Prefer the server's authoritative (half-day-aware) leave_days so a 0.5
    // leave reads "0.5 days"; fall back to a calendar span only for older
    // records that lack the field.
    if (request.leave_days != null && request.leave_days !== '') {
      return Number(request.leave_days);
    }
    const start = new Date(request.start_date);
    const end = new Date(request.end_date);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24) + 1);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...CARD_SX, p: 0, overflow: 'hidden', height: '100%',
        transition: 'all 0.25s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 28px rgba(17,24,39,0.08)' },
      }}
    >
      <Box sx={{ p: 2, bgcolor: meta.bg, borderBottom: '1px solid #eef0f4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: meta.fg }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle2" fontWeight={700}>
              {type === 'received' ? request.employee_name : request.name}
            </Typography>
          </Box>
          <Chip
            label={meta.label}
            size="small"
            sx={{ bgcolor: '#fff', color: meta.fg, fontWeight: 700, border: `1px solid ${meta.fg}33` }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Requested on {formatDate(request.created_at)}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BeachAccessIcon fontSize="small" sx={{ color: '#9ca3af' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                <Typography variant="body2" fontWeight={600}>{request.leave_type}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon fontSize="small" sx={{ color: '#9ca3af' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2" fontWeight={600}>{calculateDays()} days</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Collapse in={expanded} timeout="auto">
          <Divider sx={{ my: 2, borderColor: '#eef0f4' }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Date Range</Typography>
              <Typography variant="body2">
                {formatDate(request.start_date)} - {formatDate(request.end_date)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Reason</Typography>
              <Typography variant="body2">{request.reason}</Typography>
            </Grid>
            {request.status_reason && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">Admin Remarks</Typography>
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>{request.status_reason}</Typography>
              </Grid>
            )}
          </Grid>
        </Collapse>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          {type === 'received' && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={localStatus}
                onChange={(e) => {
                  setLocalStatus(e.target.value);
                  onStatusChange(e, request.id);
                }}
                sx={{ borderRadius: '12px', '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                MenuProps={SELECTED_MENU_PROPS}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="ACCEPTED">Accept</MenuItem>
                <MenuItem value="DECLINED">Decline</MenuItem>
              </Select>
            </FormControl>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 'auto', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

// ---- Employee's own leave history row ----
const LeaveHistoryCard = ({ request, index, tab }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = statusMeta(request.status, request.is_auto_approved);

  const calculateDays = () => {
    // Prefer the server's authoritative (half-day-aware) leave_days so a 0.5
    // leave reads "0.5 days"; fall back to a calendar span only for older
    // records that lack the field.
    if (request.leave_days != null && request.leave_days !== '') {
      return Number(request.leave_days);
    }
    const start = new Date(request.start_date);
    const end = new Date(request.end_date);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24) + 1);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '16px',
        bgcolor: '#fff',
        border: '1px solid #eef0f4',
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        '&:hover': { boxShadow: '0 8px 22px rgba(17,24,39,0.07)', borderColor: `${meta.fg}33` },
      }}
    >
      <Box sx={{ p: { xs: 1.75, sm: 2 }, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Badge badgeContent={index + 1} color="primary">
                <Avatar sx={{ width: 32, height: 32, bgcolor: `${ACCENT}14`, color: ACCENT }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="caption" color="text.secondary">Requested on</Typography>
                <Typography variant="body2" fontWeight={600}>{formatDate(request.created_at)}</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary">Leave Type</Typography>
            <Typography variant="body2" fontWeight={600}>{request.leave_type}</Typography>
          </Grid>

          {/* Leave Date — the actual day(s) off, visible without expanding. */}
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Leave Date</Typography>
            <Typography variant="body2" fontWeight={600}>
              {request.start_date === request.end_date
                ? formatDate(request.start_date)
                : `${formatDate(request.start_date)} – ${formatDate(request.end_date)}`}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2" fontWeight={600}>{calculateDays()} days</Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={meta.label}
                size="small"
                sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700 }}
              />
              {/* Withdraw/Cancel is intentionally NOT offered to employees — only
                  HR/admin may cancel a leave (from the admin Leave Requests page). */}
              <IconButton size="small">
                {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: '#eef0f4' }} />
        <Box sx={{ p: 2, bgcolor: '#fcfdff' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" display="block">Requested Date</Typography>
              <Typography variant="body2" fontWeight={500}>{formatDate(request.created_at)}</Typography>
            </Grid>

            {tab === 'accepted' && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="success.main" display="block">Accepted Date</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(request.approved_date)}</Typography>
              </Grid>
            )}
            {tab === 'rejected' && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="error.main" display="block">Rejected Date</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(request.declined_date)}</Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDate(request.start_date)} to {formatDate(request.end_date)} ({calculateDays()} days)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Typography variant="caption" color="text.secondary" display="block">Reason</Typography>
              <Typography variant="body2">{request.reason}</Typography>
            </Grid>

            {tab !== 'pending' && request.status_reason && (
              <Grid item xs={12} sm={8}>
                <Typography variant="caption" sx={{ color: ACCENT }} display="block">Admin Remarks</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{request.status_reason}</Typography>
              </Grid>
            )}

          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

const LeavesEmp = () => {
  const [component, SetComponent] = useState('applied');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Date carried in from the Attendance page (/leaves?apply=1&date=YYYY-MM-DD)
  // when an employee clicks an Absent day — pre-fills & locks the leave dates.
  const [prefillDate, setPrefillDate] = useState(null);
  const { authData } = useContext(AuthContext);
  const { mutate: updateEmpLeave } = useUpdateEmpLeaveRequest();
  const id = authData?.employee_no;
  const { leave } = useContext(leaveContext);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPrefillDate(null); // clear so a later manual "Apply" open isn't pre-filled
    SetComponent('applied');
  };

  // Deep-link from the Attendance page's LOP-days shortcut (/leaves?apply=1):
  // open the apply modal on arrival, then strip the param so a refresh or a
  // modal-close doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('apply')) {
      const d = searchParams.get('date');
      if (d) setPrefillDate(d);
      setIsModalOpen(true);
      searchParams.delete('apply');
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: empleave } = useSingleEmpLeaveDetails(id);
  const { data: leaveRequests } = useLeaveCCrequests(id);
  useLeaveRequestsforme(id);
  const { data: employeeDetails } = useGetSingleEmployee({ id });

  const emp = employeeDetails?.employee;
  const isConfirmed = emp?.employment_status === 'Confirmed' || emp?.employment_status === 'Active';

  const historyRequests = Array.isArray(empleave) ? empleave : [];
  const acceptedRequests = historyRequests.filter((r) => r.status === 'ACCEPTED');
  const pendingRequests = historyRequests.filter((r) => r.status === 'PENDING');
  const rejectedRequests = historyRequests.filter((r) => r.status === 'DECLINED');

  const handleStatusChange = async (event, leaveId) => {
    const newStatus = event.target.value;
    try {
      await updateEmpLeave({ leave_request_id: leaveId, status: newStatus });
      alert('Leave status updated successfully');
    } catch (error) {
      console.error('Error updating leave status:', error);
      alert('Error updating leave status');
    }
  };

  const renderComponent = () => {
    const renderList = (requests, emptyMessage, tabType) => {
      if (requests.length > 0) {
        return requests.map((request, index) => (
          <LeaveHistoryCard key={request.id} request={request} index={index} tab={tabType} />
        ));
      }
      return <EmptyState message={emptyMessage} />;
    };

    switch (component) {
      case 'applied': return renderList(historyRequests, 'No leave requests found.', 'applied');
      case 'accepted': return renderList(acceptedRequests, 'No accepted leave requests.', 'accepted');
      case 'pending': return renderList(pendingRequests, 'No pending leave requests.', 'pending');
      case 'rejected': return renderList(rejectedRequests, 'No rejected leave requests.', 'rejected');
      default: return renderList(historyRequests, 'No leave requests found.', 'applied');
    }
  };

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      {/* ---- Header strip (shared PageHeader) ---- */}
      <PageHeader
        icon={<BeachAccessIcon />}
        title="Leave Management"
        subtitle="Manage your leaves and track history"
        action={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2.5, sm: 4 }, px: { xs: 2, sm: 2.5 }, py: 1.5, borderRadius: '18px', bgcolor: '#f8fafc', border: '1px solid #eef0f4' }}>
            {isConfirmed ? (
              <>
                <StatPill value={emp?.total_leaves ?? 0} label="Total" dot={ACCENT} />
                <StatPill value={emp?.sick_leaves ?? 0} label="Sick" dot="#16a34a" />
                <StatPill value={emp?.casual_leaves ?? 0} label="Casual" dot="#eab308" />
              </>
            ) : (
              <StatPill value={emp?.total_leaves ?? leave ?? 0} label={`Available${emp?.employment_status ? ` · ${emp.employment_status}` : ''}`} dot="#16a34a" />
            )}
          </Box>
        )}
      />

      {/* ---- Controls: filters + Request Leave ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 }, p: { xs: 1.75, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', mr: 0.5 }}>My Leave Requests</Typography>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', flexWrap: 'wrap', rowGap: 1 }}>
              {FILTERS.map((f) => {
                const active = component === f.value;
                return (
                  <Box
                    key={f.value}
                    onClick={() => SetComponent(f.value)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.6,
                      px: 1.6, py: 0.9, borderRadius: '12px', cursor: 'pointer',
                      fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', flexShrink: 0,
                      color: active ? f.fg : '#6b7280',
                      bgcolor: active ? f.bg : 'transparent',
                      border: `1px solid ${active ? `${f.fg}33` : 'transparent'}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: active ? f.bg : '#f8fafc' },
                    }}
                  >
                    {f.icon}{f.label}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => { setPrefillDate(null); setIsModalOpen(true); }}
            sx={{
              py: 1.1, px: 2.5, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
              boxShadow: '0 8px 18px rgba(47,107,255,0.25)',
              '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
            }}
          >
            Request Leave
          </Button>
        </Box>
      </Paper>

      {/* ---- CC requests (managers) ---- */}
      {leaveRequests?.leave_requests?.length > 0 && (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon sx={{ color: ACCENT }} /> Leave Requests (CC)
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {leaveRequests.leave_requests.map((lev, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <LeaveRequestCard request={lev} onStatusChange={handleStatusChange} type="raised" />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ---- History list ---- */}
      <Paper elevation={0} sx={CARD_SX}>
        {renderComponent()}
      </Paper>

      {/* ---- Request Leave Modal (shared with the Attendance LOP-days shortcut) ---- */}
      <ApplyLeaveModal open={isModalOpen} onClose={handleCloseModal} prefillDate={prefillDate} />
    </Box>
  );
};

export default LeavesEmp;
